import React, { useEffect, useState } from 'react'
import axios from 'axios'
import Cookies from 'js-cookie'
import Topbar from '../../layouts/Topbar'
import Sidebar from '../../layouts/Sidebar'
import apiConfig, { API_BASE_URL as NAMED_API_BASE_URL } from '../../../config/api'

const API_BASE_URL = typeof apiConfig === 'string' ? apiConfig : NAMED_API_BASE_URL || 'https://pms.burrayhr.com'

const MyReviews = () => {
  const baseUrl = API_BASE_URL
  const [goals, setGoals] = useState([])
  const [filteredGoals, setFilteredGoals] = useState([])
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [availableYears, setAvailableYears] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [viewGoal, setViewGoal] = useState(null)

  useEffect(() => {
    const fetchGoals = async () => {
      setLoading(true)
      setError(null)
      try {
        const token = Cookies.get('token')
        const response = await axios.get(`${baseUrl}/api/key-result-areas/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        })
        // collect completed goals across KRAs
        const allGoals = (Array.isArray(response.data) ? response.data : [])
          .flatMap(kra => Array.isArray(kra.goals) ? kra.goals : [])
          .filter(goal => goal.status === 'complete' || goal.status === 'completed')
        
        setGoals(allGoals)
        const years = Array.from(new Set(allGoals.map(g => g?.year).filter(y => y !== undefined && y !== null))).sort((a,b)=>a-b)
        setAvailableYears(years)
        const effectiveYear = years.length && years.includes(selectedYear) ? selectedYear : (years[years.length-1] || selectedYear)
        setSelectedYear(effectiveYear)
        setFilteredGoals(allGoals.filter(g => (g.year !== undefined && g.year !== null) ? g.year === effectiveYear : true))
      } catch (err) {
        setError('Failed to fetch completed goals and reviews.')
      } finally {
        setLoading(false)
      }
    }
    fetchGoals()
  }, [baseUrl])

  useEffect(() => {
    if (goals.length > 0) {
      setFilteredGoals(goals.filter(g => (g.year !== undefined && g.year !== null) ? g.year === selectedYear : true))
    }
  }, [selectedYear, goals])

  const handleViewGoal = (goal) => {
    setViewGoal(goal)
    setViewModalOpen(true)
  }

  // Prepare data for report
  const generateReportData = () => {
    const kraMap = {}
    filteredGoals.forEach(g => {
      const kraName = (g.key_result_area && (g.key_result_area.name || g.key_result_area)) || g.kra_name || 'Uncategorized'
      if (!kraMap[kraName]) kraMap[kraName] = []
      kraMap[kraName].push(g)
    })
    const totalAgreedScore = filteredGoals.reduce((s, g) => s + (Number(g.agreed_score) || 0), 0)
    const totalMetric = filteredGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0)
    const percentage = totalMetric > 0 ? ((totalAgreedScore / totalMetric) * 100).toFixed(2) : '0.00'
    return { year: selectedYear, kras: Object.entries(kraMap), totalAgreedScore, totalMetric, percentage, goals: filteredGoals }
  }

  // Generate PDF Report
  const generatePDF = async () => {
    try {
      const report = generateReportData()
      const { jsPDF } = await import('jspdf')
      const doc = new jsPDF('p', 'mm', 'a4')

      const primary = [59, 130, 246]
      const dark = [55, 65, 81]
      const light = [156, 163, 175]

      const wrap = (text, maxWidth) => {
        const words = String(text || '').split(' ')
        const lines = []; let line = ''
        words.forEach(w => { const t = line ? (line + ' ' + w) : w; if (doc.getTextWidth(t) > maxWidth && line) { lines.push(line); line = w; } else { line = t; } })
        if (line) lines.push(line); return lines
      }

      const drawTable = (headers, rows, startY, maxWidth=180) => {
        const colWidths = [70,40,30,30,10]
        const headerH = 10; const rowH = 8; let x = 15
        doc.setFillColor(...primary); doc.rect(15, startY, maxWidth, headerH, 'F')
        headers.forEach((h, i) => { doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont(undefined, 'bold'); doc.text(h, x + 2, startY + 6); x += colWidths[i]; })
        let y = startY + headerH; doc.setTextColor(0, 0, 0); doc.setFont(undefined, 'normal'); doc.setFontSize(9)
        rows.forEach((row, rIdx) => {
          if (rIdx % 2 === 0) { doc.setFillColor(249, 250, 251); doc.rect(15, y, maxWidth, rowH, 'F'); }
          let xx = 15; let maxLines = 1
          row.forEach((cell, i) => { const wl = wrap(cell, colWidths[i] - 4); maxLines = Math.max(maxLines, wl.length); wl.forEach((ln, li) => { doc.text(ln, xx + 2, y + 5 + (li * 3.5)); }); xx += colWidths[i]; })
          y += Math.max(rowH, maxLines * 3.5 + 2)
        })
        return y
      }

      let y = 20
      doc.setTextColor(...primary); doc.setFontSize(18); doc.setFont(undefined, 'bold')
      doc.text('KPI Reviews Report', 105, y, {align:'center'})
      y += 12; doc.setTextColor(...dark); doc.setFontSize(14); doc.setFont(undefined, 'normal')
      doc.text(`Year: ${report.year}`, 105, y, {align:'center'})
      y += 16; doc.setFillColor(248, 250, 252); doc.rect(15, y, 180, 25, 'F'); doc.setDrawColor(...primary); doc.rect(15, y, 180, 25)
      y += 8; doc.setTextColor(...dark); doc.setFontSize(13); doc.setFont(undefined, 'bold'); doc.text('Summary', 20, y)
      y += 7; doc.setFontSize(11); doc.setFont(undefined, 'normal'); doc.text(`Total Final Score: ${report.totalAgreedScore} / ${report.totalMetric}`, 20, y)
      y += 5; doc.text(`Performance: ${report.percentage}%`, 20, y)
      y += 15

      report.kras.forEach(([kraName, kraGoals]) => {
        if (y > 250) { doc.addPage(); y = 20; }
        doc.setFillColor(...primary); doc.rect(15, y, 180, 12, 'F'); doc.setTextColor(255, 255, 255); doc.setFontSize(12); doc.setFont(undefined, 'bold')
        doc.text(`KRA: ${kraName} (Weight: ${kraGoals?.[0]?.key_result_area?.percentage ?? kraGoals?.[0]?.kra?.percentage ?? kraGoals?.[0]?.percentage ?? '-'}%)`, 20, y + 8); y += 15
        const headers = ['Activity/KPI', 'Standard', 'Metric', 'Agreed', 'RAG', '']
        const rows = kraGoals.map((g)=>[
          g.activity || 'N/A',
          g.performance_standard || 'N/A',
          (g.metric !== undefined ? g.metric : '%'),
          (g.agreed_score !== undefined ? g.agreed_score : '0'),
          (g.rag !== undefined ? g.rag : 'Not Reviewed'),
          ''
        ])
        const kraTotal = kraGoals.reduce((s, g) => s + (Number(g.agreed_score) || 0), 0)
        const kraMetric = kraGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0)
        rows.push(['KRA Total', '', `${kraMetric}`, `${kraTotal}`, '', ''])
        y = drawTable(headers, rows, y)
        y += 10
      })

      const pages = doc.internal.getNumberOfPages()
      for (let i = 1; i <= pages; i++) {
        doc.setPage(i)
        doc.setTextColor(...light)
        doc.setFontSize(8)
        doc.text('Generated by PMS', 15, 290)
        doc.text(`Page ${i} of ${pages}`, 195, 290, { align: 'right' })
      }

      const filename = `KPI Reviews Report ${selectedYear}.pdf`
      doc.save(filename)
    } catch (e) {
      alert('Failed to generate PDF report.')
    }
  }

  // Calculate totals for summary card
  const totalAgreed = filteredGoals.reduce((sum, g) => sum + (Number(g.agreed_score) || Number(g.hod_score) || 0), 0)
  const totalMetric = filteredGoals.reduce((sum, g) => sum + (Number(g.metric) || 0), 0)
  const performancePct = totalMetric > 0 ? ((totalAgreed / totalMetric) * 100).toFixed(2) : '0.00'

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <Topbar />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content pb-0">

          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
            <div className="my-auto mb-2 mb-md-0">
              <h4 className="page-title mb-0">My KPI Reviews</h4>
              <p className="text-muted mb-0 mt-1">Review completed Key Performance Indicators and review history.</p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <div className="d-flex align-items-center gap-2 bg-white px-3 py-1 border rounded shadow-sm">
                <i className="ti ti-calendar text-muted fs-18"></i>
                <label className="form-label mb-0 fw-semibold fs-13 text-muted me-1">Year:</label>
                <select
                  className="form-select form-select-sm border-0 bg-transparent fw-bold text-dark shadow-none cursor-pointer"
                  style={{ width: 'auto' }}
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                >
                  {(availableYears.length ? availableYears : [selectedYear]).sort((a, b) => a - b).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              {filteredGoals.length > 0 && (
                <button
                  type="button"
                  onClick={generatePDF}
                  className="btn btn-danger btn-md d-flex align-items-center gap-2 shadow-sm"
                  title="Download PDF Report"
                >
                  <i className="ti ti-file-type-pdf fs-18"></i>
                  <span className="d-none d-sm-inline">Export PDF</span>
                </button>
              )}
            </div>
          </div>

          {/* Loading / Error States */}
          {loading ? (
            <div className="card shadow-sm border-0 mb-4 py-5 text-center">
              <div className="card-body">
                <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h6 className="text-muted fw-normal mb-0">Loading your completed KPI reviews...</h6>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger d-flex align-items-center shadow-sm mb-4" role="alert">
              <i className="ti ti-alert-triangle fs-20 me-2"></i>
              <div>{error}</div>
            </div>
          ) : filteredGoals.length === 0 ? (
            <div className="card shadow-sm border-0 mb-4 py-5 text-center">
              <div className="card-body">
                <div className="avatar avatar-xl bg-light-secondary rounded-circle mb-3 mx-auto d-flex align-items-center justify-content-center" style={{ width: '64px', height: '64px' }}>
                  <i className="ti ti-clipboard-off text-muted fs-28"></i>
                </div>
                <h5 className="text-dark fw-bold mb-1">No Completed KPIs Found</h5>
                <p className="text-muted mb-0">There are no reviewed or completed KPIs available for {selectedYear}.</p>
              </div>
            </div>
          ) : (
            (() => {
              const kraMap = {}
              filteredGoals.forEach(g => {
                const kraName = (g.key_result_area && (g.key_result_area.name || g.key_result_area)) || g.kra_name || 'Uncategorized'
                if (!kraMap[kraName]) kraMap[kraName] = []
                kraMap[kraName].push(g)
              })
              const grouped = Object.entries(kraMap)

              return (
                <>
                  {/* Reviews Table Card */}
                  <div className="card shadow-sm border-0 mb-4">
                    <div className="card-header bg-transparent border-bottom py-3 d-flex align-items-center justify-content-between">
                      <h5 className="card-title mb-0 text-primary fw-bold">
                        <i className="ti ti-award me-2"></i>KPI Reviews Breakdown ({selectedYear})
                      </h5>
                      <span className="badge bg-light-primary text-primary fw-semibold fs-12 px-3 py-2">
                        {filteredGoals.length} Completed {filteredGoals.length === 1 ? 'Goal' : 'Goals'}
                      </span>
                    </div>
                    <div className="card-body p-0">
                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ minWidth: '220px' }}>Activity / KPI</th>
                              <th style={{ minWidth: '200px' }}>Performance Standard</th>
                              <th className="text-center" style={{ width: '120px' }}>RAG Indicator</th>
                              <th className="text-center" style={{ width: '90px' }}>Metric</th>
                              <th className="text-center" style={{ width: '100px' }}>Self Score</th>
                              <th className="text-center" style={{ width: '100px' }}>Supervisor/COO</th>
                              <th className="text-center" style={{ width: '100px' }}>Agreed Score</th>
                              <th className="text-center" style={{ width: '80px' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {grouped.map(([kra, kraGoals], idx) => {
                              const kraWeight = kraGoals?.[0]?.key_result_area?.percentage ?? kraGoals?.[0]?.kra?.percentage ?? kraGoals?.[0]?.percentage ?? '-'
                              const kraSubtotalMetric = kraGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0)
                              const kraSubtotalSelf = kraGoals.reduce((s, g) => s + (Number(g.score) || 0), 0)
                              const kraSubtotalHod = kraGoals.reduce((s, g) => s + (Number(g.hod_score) || 0), 0)
                              const kraSubtotalAgreed = kraGoals.reduce((s, g) => s + (Number(g.agreed_score) || 0), 0)

                              return (
                                <React.Fragment key={kra || idx}>
                                  {/* KRA Group Header */}
                                  <tr className="table-secondary bg-light-subtle">
                                    <td colSpan={8} className="fw-bold py-2 text-dark">
                                      <i className="ti ti-target me-2 text-primary"></i>
                                      <span>{kra}</span>
                                      <span className="badge bg-secondary ms-2 font-weight-normal">
                                        KRA Weight: {kraWeight}%
                                      </span>
                                    </td>
                                  </tr>

                                  {/* Goals under this KRA */}
                                  {kraGoals.map((goal, gidx) => (
                                    <tr key={goal.id || gidx}>
                                      <td>
                                        <div className="fw-semibold text-dark">{goal.activity || '[No Activity]'}</div>
                                      </td>
                                      <td>
                                        <div className="text-muted fs-13 text-truncate" style={{ maxWidth: '280px' }} title={goal.performance_standard}>
                                          {goal.performance_standard || '-'}
                                        </div>
                                      </td>
                                      <td className="text-center">
                                        {goal.progress_indicator || goal.rag_color ? (
                                          <span
                                            className="badge rounded-pill px-3 py-1 text-uppercase font-weight-bold"
                                            style={{
                                              backgroundColor: goal.progress_indicator || '#6c757d',
                                              color: '#fff',
                                              fontSize: '11px'
                                            }}
                                          >
                                            {goal.rag_color || 'Completed'}
                                          </span>
                                        ) : (
                                          <span className="badge bg-light-secondary text-secondary">N/A</span>
                                        )}
                                      </td>
                                      <td className="text-center font-weight-bold text-dark">
                                        {goal.metric !== undefined ? goal.metric : '-'}
                                      </td>
                                      <td className="text-center text-primary font-weight-bold">
                                        {goal.score !== undefined ? goal.score : '-'}
                                      </td>
                                      <td className="text-center text-info font-weight-bold">
                                        {goal.hod_score !== undefined ? goal.hod_score : '-'}
                                      </td>
                                      <td className="text-center text-success font-weight-bold">
                                        {goal.agreed_score !== undefined ? goal.agreed_score : '-'}
                                      </td>
                                      <td className="text-center">
                                        <button
                                          type="button"
                                          className="btn btn-icon btn-sm btn-outline-primary rounded-circle"
                                          onClick={() => handleViewGoal(goal)}
                                          title="View Details"
                                        >
                                          <i className="ti ti-eye"></i>
                                        </button>
                                      </td>
                                    </tr>
                                  ))}

                                  {/* KRA Subtotal Row */}
                                  <tr className="bg-light fw-bold text-dark border-bottom">
                                    <td colSpan={3} className="text-end text-muted fs-13 text-uppercase">
                                      {kra} Subtotal:
                                    </td>
                                    <td className="text-center text-dark">{kraSubtotalMetric}</td>
                                    <td className="text-center text-primary">{kraSubtotalSelf}</td>
                                    <td className="text-center text-info">{kraSubtotalHod}</td>
                                    <td className="text-center text-success">{kraSubtotalAgreed} / {kraSubtotalMetric}</td>
                                    <td></td>
                                  </tr>
                                </React.Fragment>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Summary Footer Card */}
                  <div className="card shadow-sm border-0 mb-4 bg-gradient-primary-subtle">
                    <div className="card-body p-4">
                      <div className="row align-items-center g-3">
                        <div className="col-md-6">
                          <h4 className="fw-bold mb-1 text-dark">Overall Performance Summary</h4>
                          <p className="text-muted mb-0">Overall aggregated KPI performance metric score for {selectedYear}.</p>
                        </div>
                        <div className="col-md-6 d-flex align-items-center justify-content-md-end justify-content-start gap-4">
                          <div className="text-center">
                            <span className="text-muted fs-12 text-uppercase font-weight-semibold d-block mb-1">Total Agreed Score</span>
                            <span className="fs-22 font-weight-extrabold text-dark">{totalAgreed} / {totalMetric}</span>
                          </div>
                          <div className="border-start ps-4 text-center">
                            <span className="text-muted fs-12 text-uppercase font-weight-semibold d-block mb-1">Performance Index</span>
                            <span className={`badge px-3 py-2 fs-16 rounded-pill ${
                              Number(performancePct) >= 80 ? 'bg-success-subtle text-success border border-success' :
                              Number(performancePct) >= 60 ? 'bg-warning-subtle text-warning border border-warning' :
                              'bg-danger-subtle text-danger border border-danger'
                            }`}>
                              {performancePct}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )
            })()
          )}

        </div>
      </div>

      {/* Goal Details Modal */}
      {viewModalOpen && viewGoal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header bg-light border-bottom">
                <h5 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                  <i className="ti ti-file-description text-primary fs-20"></i>
                  <span>KPI Review Details</span>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setViewModalOpen(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body p-4">
                <div className="table-responsive rounded border">
                  <table className="table table-striped table-borderless align-middle mb-0">
                    <tbody>
                      <tr>
                        <td className="fw-semibold text-muted" style={{ width: '35%' }}>Activity / KPI:</td>
                        <td className="fw-bold text-dark">{viewGoal.activity || '-'}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">Performance Standard / Target:</td>
                        <td className="text-dark">{viewGoal.performance_standard || '-'}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">Target Metric:</td>
                        <td className="fw-bold text-dark">{viewGoal.metric !== undefined ? viewGoal.metric : '-'}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">Self Score:</td>
                        <td className="fw-bold text-primary">{viewGoal.score !== undefined ? viewGoal.score : '-'}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">Supervisor / COO Score:</td>
                        <td className="fw-bold text-info">{viewGoal.hod_score !== undefined ? viewGoal.hod_score : '-'}</td>
                      </tr>
                      <tr>
                        <td className="fw-semibold text-muted">Agreed Score:</td>
                        <td className="fw-bold text-success">{viewGoal.agreed_score !== undefined ? viewGoal.agreed_score : '-'}</td>
                      </tr>
                      {viewGoal.comments && (
                        <tr>
                          <td className="fw-semibold text-muted">Review Comments:</td>
                          <td className="text-dark">{viewGoal.comments}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer bg-light border-top">
                <button
                  type="button"
                  className="btn btn-secondary px-4"
                  onClick={() => setViewModalOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MyReviews